/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import TextField, { TextFieldPresentationProps } from "@eisinc/MobiusOverrides/TextField";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    city: state.components.contactUsForm.fieldValues["city"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CompanyNameStyles {
    companyNameTextField?: TextFieldPresentationProps;
}

export const companyNameStyles: CompanyNameStyles = {
    companyNameTextField: {
        cssOverrides: {
            formField: css`
                margin-bottom: 15px;
                width: 45%;
                @media (max-width: 768px) {
                    width: 100%;
                }
            `,
        },
    },
};

const styles = companyNameStyles;

const CityField: React.FC<Props> = ({ fields, city, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [cityErrorMsg, setCityErroMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.city = validateCity;
        if (typeof city !== "undefined") {
            validateCity();
        }        
    }, [city]);

    React.useEffect(() => {
        contactFormContext.validators.city = validateCity;
    }, [isRequired]);

    const validateCity = () => {
        const errorMessage = isRequired && !city ? (siteMessage("Field_Required", label) as string) : "";
        setCityErroMessage(errorMessage);
        return !errorMessage;
    };

    const cityChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "city", value: event.target.value });
    };


    return (
        <TextField
            {...styles.companyNameTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={city || ""}
            error={cityErrorMsg}
            minLength={1}
            maxLength={100}
            onChange={cityChangeHandler}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CityField),
    definition: {
        group: "Contact Us",
        icon: "FormField",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Town/City",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter Town/City",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
